import React, { useState, useCallback } from 'react';
import {
  Form,
  Input,
  Button,
  Select,
  Row,
  Col,
  Alert,
} from 'antd';
import {
  UserOutlined,
  MailOutlined,
  IdcardOutlined,
  KeyOutlined,
  ArrowLeftOutlined,
  EyeInvisibleOutlined,
  EyeTwoTone,
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import axios from '../../../../helpers/Axios';

import SecLogo from '../../../../utility/unnamed.png';
import SecBuilding from '../../../../assets/files/img/aaa.jpg';

const authHeaders = {
  headers: {
    'X-Gravitee-Api-Key': process.env.REACT_APP_GRAVITEE_API_KEY_AUTHENTICATION,
  },
};

const { Option } = Select;

export default function ForgotPassword() {
  const navigate = useNavigate();

  const [role, setRole] = useState(null);
  const [step, setStep] = useState('request');
  const [alert, setAlert] = useState(null);

  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const [secNo, setSecNo] = useState(null);
  const [username, setUsername] = useState(null);
  const [email, setEmail] = useState(null);

  const [requestForm] = Form.useForm();
  const [codeForm] = Form.useForm();

  const showAlert = useCallback((type, message) => {
    setAlert({ type, message });
  }, []);

  const handleRoleChange = (value) => {
    if (value === '1') setRole('company');
    else if (value === '2') setRole('filer');
    else setRole(null);

    setStep('request');
    setAlert(null);
  };

  const handleRequestSubmit = async (values) => {
    if (!role) {
      showAlert('error', 'Please select a role first.');
      return;
    }

    setSending(true);
    setAlert(null);

    try {
      if (role === 'company') {
        await axios.get(
          `https://api.fortisph.space/gateway/authentication/users/password/forgot_email/`,
          {
            ...authHeaders,
            params: {
              sec_no: values.sec_no,
              username: values.username,
              email: values.email,
            },
          }
        );

        setSecNo(values.sec_no);
        setUsername(values.username);
        setEmail(values.email);
      } else {
        await axios.get(
          `https://api.fortisph.space/gateway/authentication/filers/password/forgot_email/`,
          {
            ...authHeaders,
            params: {
              username: values.username,
              email: values.email,
            },
          }
        );

        setSecNo(null);
        setUsername(values.username);
        setEmail(values.email);
      }

      showAlert('success', 'Please check your email for the validation code.');
      setStep('code');
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        'Failed to send validation code. Please try again.';
      showAlert('error', msg);
    } finally {
      setSending(false);
    }
  };

  const handleCodeSubmit = async (values) => {
    if (!role || !username || !email) {
      showAlert(
        'error',
        'Missing data from previous step. Please restart the process.'
      );
      return;
    }

    setVerifying(true);
    setAlert(null);

    try {
      if (role === 'company') {
        await axios.post(
          `https://api.fortisph.space/gateway/authentication/users/password/`,
          {},
          {
            ...authHeaders,
            params: {
              sec_no: secNo || '',
              username: encodeURIComponent(username),
              email,
              code: values.code,
              new_password: values.password1,
              confirm_password: values.password2,
            },
          }
        );
      } else {
        await axios.get(
          `https://api.fortisph.space/gateway/authentication/filers/password/`,
          {
            ...authHeaders,
            params: {
              username,
              email,
              code: values.code,
              new_password: values.password1,
              confirm_password: values.password2,
            },
          }
        );
      }

      showAlert('success', 'Please login using your new password.');
      setTimeout(() => navigate('/user/login'), 3000);
    } catch (error) {
      const msg =
        error?.response?.data?.message ||
        'Failed to reset password. Please try again.';
      showAlert('error', msg);
    } finally {
      setVerifying(false);
    }
  };

  const passwordRules = [
    { required: true, message: 'Password is required' },
    {
      pattern: /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[_#?!@$%^&*-]).{10,}$/,
      message: 'At least 10 chars, one upper, one lower, one number, one special, no spaces.',
    },
    { min: 10, message: 'Password should have at least 10 characters.' },
  ];

  return (
    <div className="relative min-h-screen overflow-x-hidden">
      {/* ── Background Image ── */}
      <img
        src={SecBuilding}
        alt=""
        aria-hidden="true"
        fetchPriority="high"
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center',
        }}
      />

      {/* ── Dark Gradient Overlay ── */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(100deg, rgba(0,36,22,0.93) 0%, rgba(0,50,32,0.82) 45%, rgba(0,30,18,0.65) 100%)',
        }}
      />

      {/* ── Main Canvas Layer ── */}
      <div
        style={{
          position: 'relative',
          zIndex: 10,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
        }}
      >
        {/* Centered Content Wrapper */}
        <div className="flex-1 flex flex-col items-center justify-center p-4 sm:p-8 md:p-12">
          
          {/* Centered Form Card */}
          <div
            className="w-full max-w-[520px] bg-white rounded-[20px] p-6 sm:p-9 my-auto"
            style={{ boxShadow: '0 24px 80px rgba(0,0,0,0.35)' }}
          >
            {/* Header / Branding Inside Card */}
            <header className="flex flex-col items-center text-center mb-6 pb-6 border-b border-gray-100">
              <img
                src={SecLogo}
                alt="SEC Logo"
                style={{ height: 60, width: 'auto', marginBottom: 12 }}
              />
              <h1 className="text-xl font-extrabold text-[#111827] mb-1">
                Reset Password
              </h1>
              <p className="text-sm text-gray-500 mb-3">
                {step === 'request'
                  ? 'Select your role and enter details to receive a validation code.'
                  : 'Enter your 6-digit validation code and your new password.'}
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    padding: '2px 10px',
                    borderRadius: 999,
                    background: 'rgba(15,130,105,0.1)',
                    color: '#0f8269',
                    border: '1px solid rgba(15,130,105,0.2)',
                  }}
                >
                  ACCOUNT RECOVERY
                </span>
              </div>
            </header>

            {alert && (
              <Alert
                type={alert.type}
                message={alert.message}
                showIcon
                closable={alert.type !== 'success'}
                onClose={() => setAlert(null)}
                style={{ marginBottom: 20, borderRadius: 10 }}
              />
            )}

            {/* Role Selection */}
            <div style={{ marginBottom: 20 }}>
              <Form layout="vertical">
                <Form.Item 
                  label={<span style={{ fontSize: 14, fontWeight: 500, color: '#374151' }}>Select Role</span>}
                >
                  <Select
                    placeholder="Choose Company or Filer"
                    size="large"
                    value={role === 'company' ? '1' : role === 'filer' ? '2' : undefined}
                    onChange={handleRoleChange}
                    disabled={step === 'code' || sending || verifying}
                    style={{ width: '100%', borderRadius: 10 }}
                  >
                    <Option value="1">Company Account</Option>
                    <Option value="2">Authorized Filer</Option>
                  </Select>
                </Form.Item>
              </Form>
            </div>

            {/* Step 1: Request Validation Code */}
            {role && step === 'request' && (
              <Form
                form={requestForm}
                layout="vertical"
                onFinish={handleRequestSubmit}
                requiredMark={false}
              >
                {role === 'company' && (
                  <Form.Item
                    name="sec_no"
                    label={<span style={{ fontSize: 14, fontWeight: 500, color: '#374151' }}>SEC Registration Number</span>}
                    rules={[
                      { required: true, message: 'SEC number is required' },
                      { min: 3, message: 'At least 3 characters' },
                      { max: 16, message: 'Maximum 16 characters' },
                      { pattern: /^[A-Z0-9-]+$/, message: 'Accepts uppercase letters, numbers, and dashes only.' },
                    ]}
                  >
                    <Input
                      prefix={<IdcardOutlined style={{ color: '#d1d5db' }} />}
                      maxLength={16}
                      placeholder="e.g. CS2019-12345"
                      size="large"
                      style={{ borderRadius: 10 }}
                    />
                  </Form.Item>
                )}

                <Form.Item
                  name="username"
                  label={<span style={{ fontSize: 14, fontWeight: 500, color: '#374151' }}>Username</span>}
                  rules={[{ required: true, message: 'Please enter your username' }]}
                >
                  <Input
                    prefix={<UserOutlined style={{ color: '#d1d5db' }} />}
                    placeholder="Enter your username"
                    size="large"
                    style={{ borderRadius: 10 }}
                  />
                </Form.Item>

                <Form.Item
                  name="email"
                  label={<span style={{ fontSize: 14, fontWeight: 500, color: '#374151' }}>Email</span>}
                  rules={[
                    { required: true, message: 'Please enter your email' },
                    { type: 'email', message: 'Invalid email format' },
                  ]}
                >
                  <Input
                    prefix={<MailOutlined style={{ color: '#d1d5db' }} />}
                    placeholder="Enter registered email address"
                    size="large"
                    style={{ borderRadius: 10 }}
                  />
                </Form.Item>

                {/* Footer Actions */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justify: 'space-between',
                    width: '100%',
                    marginTop: 24,
                    paddingTop: 20,
                    borderTop: '1px solid #f3f4f6',
                  }}
                >
                  <Link
                    to="/user/login"
                    style={{
                      color: '#0f8269',
                      fontWeight: 600,
                      fontSize: 14,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <ArrowLeftOutlined /> Back to Sign In
                  </Link>

                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={sending}
                    style={{
                      background: '#0f8269',
                      borderColor: '#0f8269',
                      borderRadius: 10,
                      height: 44,
                      padding: '0 28px',
                      fontWeight: 600,
                      fontSize: 15,
                      marginLeft: 'auto',
                    }}
                  >
                    Submit
                  </Button>
                </div>
              </Form>
            )}

            {/* Step 2: Enter code + new Password */}
            {step === 'code' && (
              <Form
                form={codeForm}
                layout="vertical"
                onFinish={handleCodeSubmit}
                requiredMark={false}
              >
                <Form.Item
                  name="code"
                  label={<span style={{ fontSize: 14, fontWeight: 500, color: '#374151' }}>6-Digit Validation Code</span>}
                  rules={[
                    { required: true, message: 'Validation code is required' },
                    { pattern: /^[0-9]{6}$/, message: 'Enter a 6-digit numeric code' },
                  ]}
                >
                  <Input
                    prefix={<KeyOutlined style={{ color: '#d1d5db' }} />}
                    maxLength={6}
                    size="large"
                    placeholder="123456"
                    style={{ borderRadius: 10, letterSpacing: '0.2em', fontWeight: 600 }}
                  />
                </Form.Item>

                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="password1"
                      label={<span style={{ fontSize: 14, fontWeight: 500, color: '#374151' }}>New Password</span>}
                      rules={passwordRules}
                    >
                      <Input.Password
                        size="large"
                        placeholder="New password"
                        style={{ borderRadius: 10 }}
                        iconRender={(visible) => visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />}
                      />
                    </Form.Item>
                  </Col>

                  <Col xs={24} sm={12}>
                    <Form.Item
                      name="password2"
                      label={<span style={{ fontSize: 14, fontWeight: 500, color: '#374151' }}>Confirm Password</span>}
                      dependencies={['password1']}
                      rules={[
                        ...passwordRules,
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (!value || getFieldValue('password1') === value) {
                              return Promise.resolve();
                            }
                            return Promise.reject(new Error('Passwords must match.'));
                          },
                        }),
                      ]}
                    >
                      <Input.Password
                        size="large"
                        placeholder="Confirm password"
                        style={{ borderRadius: 10 }}
                        iconRender={(visible) => visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                {/* Footer Actions */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justify: 'space-between',
                    width: '100%',
                    marginTop: 24,
                    paddingTop: 20,
                    borderTop: '1px solid #f3f4f6',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setStep('request');
                      setAlert(null);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#6b7280',
                      fontWeight: 500,
                      fontSize: 14,
                      cursor: 'pointer',
                      padding: 0,
                    }}
                  >
                    ← Change details
                  </button>

                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={verifying}
                    style={{
                      background: '#0f8269',
                      borderColor: '#0f8269',
                      borderRadius: 10,
                      height: 44,
                      padding: '0 28px',
                      fontWeight: 600,
                      fontSize: 15,
                      marginLeft: 'auto',
                    }}
                  >
                    Reset Password
                  </Button>
                </div>
              </Form>
            )}

            {/* Default back button before selecting a role */}
            {!role && step === 'request' && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justify: 'space-between',
                  width: '100%',
                  marginTop: 24,
                  paddingTop: 20,
                  borderTop: '1px solid #f3f4f6',
                }}
              >
                <Link
                  to="/user/login"
                  style={{
                    color: '#0f8269',
                    fontWeight: 600,
                    fontSize: 14,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <ArrowLeftOutlined /> Back to Sign In
                </Link>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}